import { API } from '@/config';
import type {
  AdvanceDeploymentRequest,
  Deployment,
  DeploymentListQuery,
  PublishVersionRequest,
  SoftwareVersion,
  StartDeploymentRequest,
  UpdateVersionRequest,
  VersionAdoption,
  VersionListQuery,
} from '@/types';
import { api } from './client';
import { getPaginated, type Paginated } from './paginated';

/**
 * Publishing builds and moving them into environments.
 *
 * Every write here requires superadmin or engineering server-side. The UI
 * disables the controls for anyone else, but that is a courtesy - the refusal
 * is the server's, and it happens whether or not the button was rendered.
 */
export const releasesService = {
  versions: (query: VersionListQuery, signal?: AbortSignal): Promise<Paginated<SoftwareVersion>> =>
    getPaginated<SoftwareVersion>(
      API.endpoints.versions,
      query,
      signal,
      'Could not load versions.',
    ),

  /**
   * Publishing is what makes a build reachable by the desktop update check, so
   * the hash and the code-signing flag are recorded here rather than trusted
   * from the client that eventually downloads it.
   */
  publish: (body: PublishVersionRequest): Promise<SoftwareVersion> =>
    api.post<SoftwareVersion>(API.endpoints.versions, body),

  /**
   * A published build's policy only: notes, whether it is required, and the
   * oldest version still allowed to run. The installer, its hash and the
   * version number never change - CI registers builds without notes, and this
   * is where they are added.
   */
  update: (versionId: string, body: UpdateVersionRequest): Promise<SoftwareVersion> =>
    api.patch<SoftwareVersion>(API.endpoints.version(versionId), body),

  /** Which versions machines run: each counted once, at its latest update check. */
  adoption: (days: number, signal?: AbortSignal): Promise<VersionAdoption[]> =>
    api.get<VersionAdoption[]>(
      `${API.endpoints.versionAdoption}?days=${String(days)}`,
      signal ? { signal } : {},
    ),

  deployments: (query: DeploymentListQuery, signal?: AbortSignal): Promise<Paginated<Deployment>> =>
    getPaginated<Deployment>(
      API.endpoints.deployments,
      query,
      signal,
      'Could not load deployments.',
    ),

  start: (body: StartDeploymentRequest): Promise<Deployment> =>
    api.post<Deployment>(API.endpoints.deployments, body),

  /**
   * Stages are set explicitly rather than inferred, so a deployment stuck
   * part-way reads as stuck instead of being silently assumed complete.
   */
  advance: (deploymentId: string, body: AdvanceDeploymentRequest): Promise<Deployment> =>
    api.patch<Deployment>(API.endpoints.deployment(deploymentId), body),

  /**
   * The second approval a stable production rollout needs when the ops
   * settings ask for one. The server refuses the person who started it.
   */
  approve: (deploymentId: string): Promise<Deployment> =>
    api.post<Deployment>(API.endpoints.deploymentApprove(deploymentId), {}),
};
